import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	VStack,
} from "@chakra-ui/react";
import { Paperclip } from "lucide-react";

import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { FormLabel } from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import { FormCard } from "../../components/Core/FormCard";
import OutlineButton from "../../components/Core/OutlineButton";
import TextAreaNew from "../../components/Core/TextArea/TextAreaNew";
import { cn } from "@/lib/utils";
import CompetencePillsController from "../../components/Shared/CompetencePills/CompetencePillsController";
import { ContactPersonController } from "../../components/Shared/ContactPerson/ContactPerson";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import { SIDE_MENU_WIDTH } from "../../components/Shared/SideMenu/SideMenu";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import { CompetenceItem } from "../../models/Competence";
import Job, { FileData, UpdateJobRequest } from "../../models/Job";
import { useCustomer, useCustomerUsersByCustomer } from "../../networking/customer.hooks";
import { useUpdateJob } from "../../networking/job.hooks";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldFormValues,
	customFieldsSchema,
	inheritedCustomFieldsFromCustomer,
	inheritedCustomFieldsFromResolved,
	jobFormCustomFieldDefinitions,
} from "../../components/Shared/CustomFields";
import JobRequirementsEditor, {
	useEditableJobRequirements,
} from "./components/JobRequirementsEditor";
import contractPersonsSchema from "../../schemas/contractPersonsSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import shiftsSchema from "../../schemas/shiftsSchema";
import { removeUndefined } from "../../utils/object.utils";
import { invoiceReferenceHint } from "../../utils/job.utils";
import FileImporter from "../../components/Shared/FileImporter/FileImporter";
import FileThumbnail from "../../components/Shared/FileImporter/FileThumbnail";
import { useMediaStore } from "../../stores/media.store";

/** Applied to a FormCard whose fields failed validation (same as the create page). */
const INVALID_SECTION_CLASS = "border-red-500 bg-red-50/40";

const JobInputsValidationSchema = yup.object().shape({
	address: yup.object().shape({
		postalCode: postalCodeSchema,
	}),
	competence: yup.object().shape({
		branch: yup.string().required("BRANCH_IS_REQUIRED"),
		service: yup.string().required("SERVICE_IS_REQUIRED"),
	}),
	contactPersons: contractPersonsSchema,
	shifts: shiftsSchema,
});

const JobOverviewTab: React.FC<{ job: Job; competences: CompetenceItem[] }> = ({
	job,
	competences,
}) => {
	const { customerUsers } = useCustomerUsersByCustomer({
		customerId: job.customer.id,
		sort: "firstName",
	});

	const { customer } = useCustomer(job.customer.id);

	const mediaStore = useMediaStore();
	const { documentList, document, documentLoading } = mediaStore;

	const { t } = useTranslation();

	const { mutate: updateJob } = useUpdateJob();

	const { data: organisation } = useOrganisation();

	// Org-defined custom fields: the job's own plus the customer fields the job
	// inherits (required-ness is form-only).
	const jobCustomFieldDefinitions = useCustomFieldDefinitions("job");
	const customerCustomFieldDefinitions = useCustomFieldDefinitions("customer");
	const customFieldDefinitions = useMemo(
		() => jobFormCustomFieldDefinitions(jobCustomFieldDefinitions, customerCustomFieldDefinitions),
		[jobCustomFieldDefinitions, customerCustomFieldDefinitions]
	);
	// The customer's values for its inheritable fields. The job GET carries the
	// resolved map, but a key the job overrides resolves to the job's value
	// there — so once the customer has loaded its own map is used, keeping the
	// "Ärvd från kund: …" hint truthful for overridden fields too.
	const inheritedCustomFields = useMemo(
		() =>
			customer
				? inheritedCustomFieldsFromCustomer(customer, customerCustomFieldDefinitions)
				: inheritedCustomFieldsFromResolved(job.resolvedCustomFields),
		[customer, customerCustomFieldDefinitions, job.resolvedCustomFields]
	);
	const validationSchema = useMemo(
		() =>
			JobInputsValidationSchema.shape({
				customFields: customFieldsSchema(customFieldDefinitions, inheritedCustomFields),
			}),
		[customFieldDefinitions, inheritedCustomFields]
	);

	const { register, handleSubmit, control, reset, formState, watch } = useForm<
		Omit<Job, "customFields"> & { customFields?: CustomFieldFormValues }
	>({
		mode: "onBlur",
		defaultValues: {
			competence: job.competence || { branch: "", service: "" },
			contactPersons: [],
			shifts: [],
			customFields: {},
		},
		resolver: yupResolver(validationSchema),
	});

	useUnsavedChangesInFormChecker(formState);

	// The job's MATCHING GATE (job.requirements) — seeded from the job's actual
	// snapshot, freely editable, and re-suggested from the org's matchningsregler
	// when the competence changes (manual additions are kept). Customer-tag
	// scoped rules resolve against the customer's tags — `undefined` until the
	// customer has loaded defers the derivation.
	const watchedCompetence = watch("competence");
	const customerTags = customer ? customer.tags ?? [] : undefined;
	const { requirements, setRequirements, resetRequirements } = useEditableJobRequirements(
		organisation,
		watchedCompetence,
		customerTags
	);
	// Only shown when a competence is selected (always true for a saved job).
	const showRequirementsEditor =
		!!watchedCompetence?.branch &&
		!!watchedCompetence?.service &&
		((organisation?.certificationTypes?.length ?? 0) > 0 ||
			(organisation?.consultantTagTypes?.length ?? 0) > 0 ||
			requirements.certifications.length > 0 ||
			requirements.tags.length > 0);

	const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
	const [imageFiles, setImageFiles] = useState<FileData[]>([]);
	const [documentFiles, setDocumentFiles] = useState<FileData[]>([]);

	const fileUploadRules = [
		{
			validTypes: ["image/png", "image/jpeg"],
			maxQuantity: 2,
			warningMessage: `${t("MAXIMUM_IMAGE_COUNT")} 2`,
			currentQuantity: imageFiles.length ?? 0,
		},
		{
			validTypes: ["application/pdf"],
			maxQuantity: 2,
			warningMessage: `${t("MAXIMUM_DOCUMENT_COUNT")} 2`,
			currentQuantity: documentFiles.length ?? 0,
		},
	];

	useEffect(() => {
		// Custom field values are re-shaped for the inputs (numbers → text).
		reset({
			...job,
			customFields: customFieldFormValues(customFieldDefinitions, job.customFields),
		});
		resetRequirements(job.requirements);
	}, [reset, resetRequirements, job, customFieldDefinitions]);

	useEffect(() => {
		if (job?.images) {
			setImageFiles(job.images);
		}
		if (job?.documents) {
			setDocumentFiles(job.documents);
		}
	}, [job]);

	const onSubmit = handleSubmit((data) => {
		if (data.competence?.skills?.length === 0) delete data.competence.skills;

		const updateOrderRequest: UpdateJobRequest = {
			address: data.address,
			competence: data.competence,
			// Always sent explicitly (even when empty) — otherwise the backend
			// re-derives the gate from the org rules on competence change and
			// manual edits would be wiped.
			requirements,
			description: data.description,
			customerDescription: data.customerDescription,
			internalNotes: data.internalNotes,
			purchaseOrderNo: data.purchaseOrderNo,
			invoiceReference: data.invoiceReference,
			customerOrderNumber: data.customerOrderNumber,
			gflRate: data.gflRate || 0,
			images: imageFiles.length > 0 ? imageFiles : undefined,
			documents: documentFiles.length > 0 ? documentFiles : undefined,
			// Always the full map — the backend replaces it wholesale.
			customFields: buildCustomFieldsPayload(data.customFields, customFieldDefinitions),
		};

		if (data.contactPersons.length) {
			if (data.contactPersons[0].profile) removeUndefined(data.contactPersons[0].profile);
			removeUndefined(data.contactPersons[0]);

			updateOrderRequest.contactPersons = data.contactPersons;
		}

		if (job?.id) {
			updateJob({ jobId: job?.id, update: updateOrderRequest });
		}
	});

	const fileRemoveHandler = (fileUrl: string) => {
		if (imageFiles.find((f) => f.url === fileUrl)) {
			setImageFiles((prev) => prev.filter((fi) => fi.url !== fileUrl));
		} else {
			setDocumentFiles((prev) => prev.filter((df) => df.url !== fileUrl));
		}
	};

	const addAddFileHandler = () => {
		const listData = documentList ?? [];
		if (document) {
			listData.push(document);
		}
		listData?.forEach((d) => {
			if (d.mimeType === "application/pdf") {
				setDocumentFiles((prev) => [...prev, { url: d.url, name: d.originalName }]);
			}
			if (["image/png", "image/jpeg"].includes(d.mimeType)) {
				setImageFiles((prev) => [...prev, { url: d.url, name: d.originalName }]);
			}
		});
		mediaStore.removeDocumentList();
		mediaStore.removeDocument();
	};

	const attachedFiles = [...imageFiles, ...documentFiles];

	return (
		<form onSubmit={onSubmit}>
			{/* Same width and card rhythm as the create page. */}
			<div className="mx-auto flex max-w-[1120px] flex-col gap-5 py-6 pb-28">
				{/* 1. Competence + the matching gate it drives */}
				<FormCard
					title="MATCHING"
					className={cn(formState.errors?.competence && INVALID_SECTION_CLASS)}
				>
					<div className="flex flex-col gap-5">
						{competences && competences.length > 0 && (
							<CompetencePillsController
								competences={competences}
								name="competence"
								control={control}
								errors={formState.errors?.competence}
							/>
						)}

						{showRequirementsEditor && (
							<div className="space-y-2">
								<p className="mb-0 text-sm font-medium text-muted-foreground">
									Certifikat & taggar
								</p>
								<JobRequirementsEditor
									organisation={organisation}
									competence={watchedCompetence}
									customerTags={customerTags}
									value={requirements}
									onChange={setRequirements}
								/>
							</div>
						)}
					</div>
				</FormCard>

				{/* 2. Address and contact person side by side on wide screens */}
				<FormCard
					title="Plats & kontaktperson"
					skipTranslation
					className={cn(
						(formState.errors?.contactPersons || formState.errors?.address) && INVALID_SECTION_CLASS
					)}
				>
					<div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
						{/* Mirrors the ContactPerson block's rhythm: an h-9 header row
						    followed by gap-3 rows, so the two columns' inputs line up. */}
						<div className="flex flex-col gap-3">
							<div className="flex h-9 items-center">
								<FormLabel margin="0">{t("ADDRESS")}</FormLabel>
							</div>
							<AppInputNew {...register("address.address1")} placeholder="ADDRESS_PLACEHOLDER" />
							<div className="grid grid-cols-2 gap-x-4">
								<AppInputNew
									{...register("address.postalCode")}
									placeholder="POSTAL_CODE_PLACEHOLDER"
									errors={formState.errors?.address?.postalCode?.message}
								/>
								<AppInputNew {...register("address.city")} placeholder="CITY_PLACEHOLDER" />
							</div>
						</div>

						<ContactPersonController
							name="contactPersons"
							control={control}
							errors={formState.errors?.contactPersons}
							customerUsers={customerUsers || []}
						/>
					</div>
				</FormCard>

				{/* 3. Descriptions, notes and attachments */}
				<FormCard
					title="JOB_DESCRIPTION"
					action={
						<OutlineButton
							label={`${t("UPLOAD_DOCUMENTS")}${
								attachedFiles.length > 0 ? ` (${attachedFiles.length})` : ""
							}`}
							icon={<Paperclip size={14} />}
							onClick={() => setUploadModalVisible(true)}
						/>
					}
				>
					<div className="flex flex-col gap-4">
						<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
							<TextAreaNew
								{...register("description")}
								label="JOB_DESCRIPTION"
								errors={formState.errors?.description?.message}
								height="120px"
							/>
							<TextAreaNew
								{...register("customerDescription")}
								label="ABOUT_WORKPLACE"
								errors={formState.errors?.customerDescription?.message}
								height="120px"
							/>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
							<TextAreaNew
								{...register("internalNotes")}
								label="job.internalNotes"
								height="100px"
							/>
							{/* The customer's own notes, for context — edited on the customer. */}
							<TextAreaNew
								value={customer?.internalNotes ?? ""}
								readOnly
								disabled
								label="customer.internalNotes"
								height="100px"
							/>
						</div>

						{attachedFiles.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{attachedFiles.map((f) => (
									<FileThumbnail
										key={f.url}
										onRemove={fileRemoveHandler}
										fileName={f.name}
										fileUrl={f.url}
									/>
								))}
							</div>
						)}
					</div>
				</FormCard>

				{/* 4. Booking & references — the booking type is fixed once the job
				    exists, so it is shown read-only next to the editable references. */}
				<FormCard title="Bokning & referenser" skipTranslation>
					{/* One two-column grid for everything on the card: the built-in inputs
					    and the custom fields (incl. inherited customer fields) flow as cells,
					    so the card grows row by row however many fields the org adds. */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2">
						<div className="flex flex-col">
							<FormLabel>{t("BOOKING_TYPE")}</FormLabel>
							<div className="flex h-[2.3125rem] items-center rounded-sm border border-solid border-gray-200 bg-gray-50 px-3 text-[0.9375rem] text-gray-700">
								{job.bookingType ? t(`BOOKING_TYPE_${job.bookingType}` as any) : "—"}
							</div>
						</div>
						<AppInputNew {...register("purchaseOrderNo")} label="job.purchaseOrderNo" />
						<AppInputNew
							{...register("invoiceReference")}
							label="job.invoiceReference"
							description={invoiceReferenceHint(t, customer?.invoiceDetails?.reference)}
						/>
						<AppInputNew {...register("customerOrderNumber")} label="job.customerOrderNo" />
						<AppInputNew
							{...register("gflRate", { valueAsNumber: true })}
							label="job.gflRate"
							type="number"
						/>
						<CustomFieldsSection
							entity="job"
							placement="bokning"
							variant="inline"
							className="contents"
							definitions={customFieldDefinitions}
							inherited={inheritedCustomFields}
							control={control}
							register={register}
							errors={formState.errors}
						/>
					</div>
				</FormCard>

				{/* 5. Org-defined custom fields placed under "Övriga uppgifter" — hidden when there are none. */}
				<CustomFieldsSection
					entity="job"
					placement="ovrigt"
					definitions={customFieldDefinitions}
					inherited={inheritedCustomFields}
					control={control}
					register={register}
					errors={formState.errors}
					className={cn(formState.errors?.customFields && INVALID_SECTION_CLASS)}
				/>
			</div>

			<FloatingButtonPanel leftMargin={SIDE_MENU_WIDTH + 310 /* job header width */}>
				<Button width="9.75rem">{t("SAVE_CHANGES")}</Button>
			</FloatingButtonPanel>

			<Modal size={"xl"} isOpen={uploadModalVisible} onClose={() => setUploadModalVisible(false)}>
				<ModalOverlay />
				<ModalContent p={4}>
					<ModalHeader textAlign="center">{t("UPLOAD_JOB_DOCUMENTS")}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FileImporter
							maxFiles={4}
							multipleFiles
							maxFileSize={20000000}
							acceptedTypes={["image/png", "application/pdf", "image/jpeg"]}
							uploadRules={fileUploadRules}
						/>
					</ModalBody>

					<VStack>
						<Button
							disabled={documentLoading || documentList?.length === 0}
							width="220px"
							margin="2.5rem 0 0 0"
							fontSize="s"
							height="34px"
							loading={documentLoading}
							onClick={() => {
								addAddFileHandler();
								setUploadModalVisible(false);
							}}
						>
							{t("UPLOAD")}
						</Button>
						<Button
							onClick={() => setUploadModalVisible(false)}
							width="220px"
							margin="0.625rem 0 0 0"
							textColor="black"
							backgroundColor="buttonGray"
							fontSize="s"
							height="34px"
						>
							{t("CANCEL")}
						</Button>
					</VStack>
				</ModalContent>
			</Modal>
		</form>
	);
};
export default JobOverviewTab;
